import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

// ─── Style constants (preserved from original) ────────────────────────────────
const inp = "w-full border border-slate-300 rounded-lg px-3 py-2 text-sm";
const lbl = "text-xs font-medium text-slate-500 mb-1 block";
const STATUS_TONE = {
  connected: "bg-emerald-50 text-emerald-700",
  available: "bg-blue-50 text-blue-700",
  not_configured: "bg-amber-50 text-amber-700",
  planned: "bg-slate-100 text-slate-500",
};

// ─── Phase B: Authorization health indicator ──────────────────────────────────
function AuthHealthBadge({ tokenValid, connected }) {
  if (!connected) return null;
  if (tokenValid === false)
    return (
      <span
        data-testid="gcal-auth-health-expired"
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-rose-50 text-rose-600 border border-rose-200"
      >
        <Icons.AlertCircle className="h-3 w-3" /> Authorization expired
      </span>
    );
  if (tokenValid === true)
    return (
      <span
        data-testid="gcal-auth-health-valid"
        className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200"
      >
        <Icons.ShieldCheck className="h-3 w-3" /> Authorization valid
      </span>
    );
  // null = not yet validated (pre-Phase-B connection)
  return (
    <span
      data-testid="gcal-auth-health-unverified"
      className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-slate-100 text-slate-500 border border-slate-200"
    >
      <Icons.Clock className="h-3 w-3" /> Not yet verified
    </span>
  );
}

// ─── Phase B: Calendar selector dropdown ──────────────────────────────────────
function CalendarSelector({ calendars, selectedId, onSelect, saving }) {
  if (!calendars || !calendars.length) return null;
  const ACCESS_ROLE_LABEL = { owner: "Owner", writer: "Can edit" };
  return (
    <div className="space-y-1.5">
      <label className="text-xs font-medium text-slate-600 block">
        Preferred calendar for new appointments
      </label>
      <select
        value={selectedId || "primary"}
        onChange={(e) => onSelect(e.target.value)}
        disabled={saving}
        data-testid="gcal-calendar-selector"
        className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 focus:border-blue-500 disabled:opacity-60"
      >
        {calendars.map((cal) => (
          <option key={cal.id} value={cal.id}>
            {cal.summary}
            {cal.primary ? " (Primary)" : ""}
            {cal.access_role
              ? ` — ${ACCESS_ROLE_LABEL[cal.access_role] || cal.access_role}`
              : ""}
          </option>
        ))}
      </select>
      <p className="text-[11px] text-slate-400">
        Only calendars you can edit are shown. Events will be created in the
        selected calendar.
      </p>
    </div>
  );
}

// ─── Phase B: Warning when switching calendar with existing synced appointments
function CalendarChangedWarning({ count, previousId, newId }) {
  if (!count || previousId === newId) return null;
  return (
    <div
      className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2.5 flex gap-2 items-start"
      data-testid="gcal-calendar-change-warning"
    >
      <Icons.Info className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
      <p className="text-xs text-amber-700">
        <strong>
          {count} existing appointment{count !== 1 ? "s" : ""}
        </strong>{" "}
        remain synced to your previous calendar. They will not be moved
        automatically. Use the <strong>Retry sync</strong> action on each
        appointment to move them to the new calendar.
      </p>
    </div>
  );
}

// ─── Phase B: Reconnect instructions for expired/revoked auth ─────────────────
function ReconnectInstructions({ show, onReconnect, busy }) {
  if (!show) return null;
  return (
    <div
      className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2.5 space-y-2"
      data-testid="gcal-reconnect-instructions"
    >
      <div className="flex items-start gap-2">
        <Icons.Link2Off className="h-4 w-4 text-rose-600 shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-medium text-rose-700">
            Google Calendar authorization has expired or been revoked.
          </p>
          <p className="text-[11px] text-rose-600 mt-0.5">
            New appointments will not sync until you reconnect. Existing synced
            appointments are not affected.
          </p>
        </div>
      </div>
      <button
        onClick={onReconnect}
        disabled={busy}
        data-testid="gcal-reconnect-btn"
        className="text-xs font-medium bg-rose-600 hover:bg-rose-700 text-white rounded-lg px-3 py-1.5 disabled:opacity-50 transition-colors"
      >
        {busy ? (
          <Icons.Loader2 className="h-3.5 w-3.5 animate-spin inline mr-1" />
        ) : (
          <Icons.RefreshCw className="h-3.5 w-3.5 inline mr-1" />
        )}
        Reconnect Google Calendar
      </button>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function SchedulingSettings() {
  // Firm-wide settings
  const [s, setS] = useState(null);
  // Integration providers list (for provider status grid)
  const [providers, setProviders] = useState([]);
  // Google Calendar Phase A/B state
  const [gcalStatus, setGcalStatus] = useState(null);
  const [calendars, setCalendars] = useState([]);
  const [selectedCalendarId, setSelectedCalendarId] = useState(null);
  const [calendarChangedInfo, setCalendarChangedInfo] = useState(null);
  const [gcalBusy, setGcalBusy] = useState(false);
  const [savingCal, setSavingCal] = useState(false);
  // URL search params (OAuth callback signals)
  const [sp, setSp] = useSearchParams();

  // ── Load functions ──────────────────────────────────────────────────────────
  const loadGcalStatus = () =>
    api
      .get("/scheduling/google/status")
      .then((r) => {
        setGcalStatus(r.data);
        setSelectedCalendarId(r.data.calendar_id || null);
      })
      .catch(() => setGcalStatus({ configured: false, connected: false }));

  const loadCalendars = () =>
    api
      .get("/scheduling/google/calendars")
      .then((r) => {
        setCalendars(r.data.calendars || []);
        if (!selectedCalendarId && r.data.selected_calendar_id) {
          setSelectedCalendarId(r.data.selected_calendar_id);
        }
      })
      .catch(() => {});

  // ── Mount: load settings, providers, and Google Calendar status ─────────────
  useEffect(() => {
    api
      .get("/scheduling/settings")
      .then((r) => setS(r.data))
      .catch(() => setS({}));
    api
      .get("/scheduling/integrations")
      .then((r) => setProviders(r.data.providers || []))
      .catch(() => {});
    loadGcalStatus();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── OAuth callback handling from URL params ─────────────────────────────────
  useEffect(() => {
    const g = sp.get("gcal");
    if (!g) return;
    if (g === "connected") {
      toast.success(
        "Google Calendar connected — your appointments will now sync."
      );
      // Fetch updated calendar list now that we have a new connection
      setTimeout(() => loadCalendars(), 800);
    } else if (g === "error") {
      toast.error("Google Calendar connection failed. Please try again.");
    } else if (g === "invalid_state") {
      toast.error("Connection expired. Please try connecting again.");
    }
    setSp({}, { replace: true });
    loadGcalStatus();
  }, [sp, setSp]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── When connected status is established, load the calendar list ─────────────
  useEffect(() => {
    if (gcalStatus?.connected) loadCalendars();
  }, [gcalStatus?.connected]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Handlers ────────────────────────────────────────────────────────────────
  const connectGcal = async () => {
    setGcalBusy(true);
    try {
      // GET /scheduling/google/authorize returns the OAuth authorization_url
      const r = await api.get("/scheduling/google/authorize");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Could not start Google connection"
      );
      setGcalBusy(false);
    }
  };

  const disconnectGcal = async () => {
    if (
      !window.confirm(
        "Disconnect Google Calendar? Future appointments will not sync until you reconnect."
      )
    )
      return;
    setGcalBusy(true);
    try {
      await api.post("/scheduling/google/disconnect");
      toast.success("Google Calendar disconnected");
      setGcalStatus((prev) => ({
        ...prev,
        connected: false,
        token_valid: null,
        calendar_id: null,
        calendar_summary: null,
      }));
      setCalendars([]);
      setCalendarChangedInfo(null);
    } catch {
      toast.error("Failed to disconnect");
    } finally {
      setGcalBusy(false);
    }
  };

  const handleCalendarSelect = async (calendarId) => {
    if (calendarId === selectedCalendarId) return;
    setSavingCal(true);
    try {
      const r = await api.put("/scheduling/google/calendar", {
        calendar_id: calendarId,
      });
      const prev = selectedCalendarId;
      setSelectedCalendarId(calendarId);
      setGcalStatus((prev_s) => ({
        ...prev_s,
        calendar_id: calendarId,
        calendar_summary: r.data.calendar_summary,
      }));
      if (r.data.existing_synced_on_previous_calendar > 0) {
        setCalendarChangedInfo({
          count: r.data.existing_synced_on_previous_calendar,
          previousId: prev,
        });
      }
      toast.success(`Calendar updated to "${r.data.calendar_summary}".`);
    } catch (e) {
      toast.error(
        e.response?.data?.detail || "Could not update calendar selection."
      );
    } finally {
      setSavingCal(false);
    }
  };

  const save = async () => {
    try {
      await api.put("/scheduling/settings", s);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save settings");
    }
  };

  const runReminders = async () => {
    try {
      const r = await api.post("/scheduling/reminders/run");
      toast.success(`${r.data.reminders_sent} reminder(s) sent`);
    } catch {
      toast.error("Failed to run reminder engine");
    }
  };

  // ── Derived state ────────────────────────────────────────────────────────────
  const isConnected = gcalStatus?.connected;
  const isConfigured = gcalStatus?.configured;
  const tokenValid = gcalStatus?.token_valid;
  const authExpired = tokenValid === false;

  // Filter out google_calendar from the generic providers grid — it has its own card
  const otherProviders = providers.filter((p) => p.key !== "google_calendar");

  // ── Loading state (settings not yet loaded) ──────────────────────────────────
  if (!s)
    return (
      <div className="p-10 text-center">
        <Icons.Loader2 className="h-6 w-6 animate-spin text-blue-500 inline" />
      </div>
    );

  return (
    <div
      className="p-6 max-w-[900px] mx-auto space-y-5"
      data-testid="scheduling-settings"
    >
      {/* ── Page header ──────────────────────────────────────────────────────── */}
      <div>
        <h1 className="text-2xl font-bold text-slate-900">
          Scheduling Settings
        </h1>
        <p className="text-sm text-slate-500">
          Firm-wide defaults, Zoom, and integrations.
        </p>
      </div>

      {/* ── Defaults card ────────────────────────────────────────────────────── */}
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-3"
        data-testid="settings-defaults-card"
      >
        <h2 className="font-heading font-semibold text-slate-800">Defaults</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div>
            <label className={lbl}>Hours start</label>
            <input
              type="time"
              className={inp}
              value={s.business_hours?.start || "09:00"}
              data-testid="settings-hours-start"
              onChange={(e) =>
                setS({
                  ...s,
                  business_hours: {
                    ...s.business_hours,
                    start: e.target.value,
                  },
                })
              }
            />
          </div>
          <div>
            <label className={lbl}>Hours end</label>
            <input
              type="time"
              className={inp}
              value={s.business_hours?.end || "17:00"}
              data-testid="settings-hours-end"
              onChange={(e) =>
                setS({
                  ...s,
                  business_hours: { ...s.business_hours, end: e.target.value },
                })
              }
            />
          </div>
          <div>
            <label className={lbl}>Time zone</label>
            <input
              className={inp}
              value={s.timezone || ""}
              data-testid="settings-timezone"
              onChange={(e) => setS({ ...s, timezone: e.target.value })}
            />
          </div>
          <div>
            <label className={lbl}>Max meetings/day</label>
            <input
              type="number"
              className={inp}
              value={s.max_daily_meetings || ""}
              data-testid="settings-max-meetings"
              onChange={(e) =>
                setS({ ...s, max_daily_meetings: Number(e.target.value) })
              }
            />
          </div>
        </div>
        <div>
          <label className={lbl}>
            Zoom host email (the licensed Zoom user meetings are created under)
          </label>
          <input
            className={inp}
            data-testid="zoom-host-email"
            value={s.zoom_host_email || ""}
            onChange={(e) => setS({ ...s, zoom_host_email: e.target.value })}
            placeholder="host@expattaxfirm.com"
          />
        </div>
        <button
          onClick={save}
          data-testid="settings-save"
          className="bg-[#1f6feb] text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-blue-700 transition-colors"
        >
          Save Settings
        </button>
      </div>

      {/* ── Integrations card ─────────────────────────────────────────────────── */}
      <div
        className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 space-y-4"
        data-testid="settings-integrations-card"
      >
        {/* Integrations header */}
        <div className="flex items-center justify-between">
          <h2 className="font-heading font-semibold text-slate-800">
            Integrations
          </h2>
          <button
            onClick={runReminders}
            data-testid="run-reminders"
            className="text-xs inline-flex items-center gap-1.5 border border-slate-200 rounded-lg px-3 py-1.5 text-slate-600 hover:bg-slate-50 transition-colors"
          >
            <Icons.BellRing className="h-3.5 w-3.5" />
            Run Reminder Engine
          </button>
        </div>

        {/* ── Google Calendar dedicated card (Phase B) ─────────────────────── */}
        <div
          className="border border-slate-200 rounded-xl overflow-hidden"
          data-testid="gcal-card"
        >
          {/* GCal card header */}
          <div className="px-4 py-3 bg-slate-50 border-b border-slate-100 flex items-center gap-2">
            <div className="h-7 w-7 rounded-lg bg-blue-50 flex items-center justify-center shrink-0">
              <Icons.CalendarDays className="h-4 w-4 text-blue-600" />
            </div>
            <div className="flex-1 min-w-0">
              <span className="text-sm font-medium text-slate-800">
                Google Calendar
              </span>
              <span className="text-[11px] text-slate-400 ml-1.5">
                per-staff personal connection
              </span>
            </div>
            {isConnected && (
              <span data-testid="gcal-auth-health">
                <AuthHealthBadge
                  tokenValid={tokenValid}
                  connected={isConnected}
                />
              </span>
            )}
          </div>

          {/* GCal card body */}
          <div className="px-4 py-4 space-y-3">
            {/* Not configured */}
            {!isConfigured && (
              <div
                className="rounded-lg bg-slate-50 border border-slate-200 px-3 py-2.5 text-xs text-slate-500"
                data-testid="gcal-not-configured"
              >
                Google Calendar integration is not configured for this
                environment. Contact your administrator to set up OAuth
                credentials.
              </div>
            )}

            {/* Configured but not connected */}
            {isConfigured && !isConnected && (
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <p className="text-sm text-slate-500 max-w-md">
                  Connect your Google account to automatically push your
                  appointments (with the Zoom link) to your personal Google
                  Calendar. Each staff member connects their own account.
                </p>
                <button
                  onClick={connectGcal}
                  disabled={gcalBusy}
                  data-testid="gcal-connect"
                  className="shrink-0 text-sm bg-[#1f6feb] hover:bg-blue-700 text-white rounded-lg px-4 py-2 flex items-center gap-2 disabled:opacity-60 transition-colors"
                >
                  {gcalBusy ? (
                    <Icons.Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Icons.Link className="h-4 w-4" />
                  )}
                  Connect Google Calendar
                </button>
              </div>
            )}

            {/* Configured and connected */}
            {isConfigured && isConnected && (
              <div className="space-y-3">
                {/* Reconnect instructions shown when auth is expired */}
                <ReconnectInstructions
                  show={authExpired}
                  onReconnect={connectGcal}
                  busy={gcalBusy}
                />

                {!authExpired && (
                  <>
                    {/* Connected status line */}
                    <div className="flex items-center gap-2 text-sm text-slate-600">
                      <Icons.CheckCircle2 className="h-4 w-4 text-emerald-500 shrink-0" />
                      <span>
                        Connected
                        {gcalStatus?.calendar_summary
                          ? ` · ${gcalStatus.calendar_summary}`
                          : ""}
                        {gcalStatus?.connected_at && (
                          <span className="text-slate-400 ml-1.5 text-xs">
                            since{" "}
                            {new Date(
                              gcalStatus.connected_at
                            ).toLocaleDateString()}
                          </span>
                        )}
                      </span>
                    </div>

                    {/* Phase B: Calendar selector — only when multiple writable calendars */}
                    {calendars.length > 1 && (
                      <CalendarSelector
                        calendars={calendars}
                        selectedId={selectedCalendarId}
                        onSelect={handleCalendarSelect}
                        saving={savingCal}
                      />
                    )}

                    {/* Only one writable calendar — show it as read-only label */}
                    {calendars.length === 1 && (
                      <div className="text-xs text-slate-500">
                        <Icons.Calendar className="h-3 w-3 inline mr-1" />
                        Syncing to:{" "}
                        <strong>{calendars[0].summary}</strong>
                      </div>
                    )}

                    {/* Phase B: Warning when calendar was changed */}
                    <CalendarChangedWarning
                      count={calendarChangedInfo?.count}
                      previousId={calendarChangedInfo?.previousId}
                      newId={selectedCalendarId}
                    />
                  </>
                )}

                {/* Disconnect — always available when connected */}
                <div className="pt-1 border-t border-slate-100 flex items-center justify-between">
                  <button
                    onClick={disconnectGcal}
                    disabled={gcalBusy}
                    data-testid="gcal-disconnect"
                    className="text-xs text-slate-400 hover:text-rose-500 transition-colors disabled:opacity-50"
                  >
                    <Icons.Unlink className="h-3.5 w-3.5 inline mr-1" />
                    Disconnect Google Calendar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Other integration providers (Zoom, M365, Teams, etc.) ─────────── */}
        {otherProviders.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {otherProviders.map((p) => (
              <div
                key={p.key}
                className="flex items-center justify-between border border-slate-200 rounded-lg px-3 py-2.5"
                data-testid={`integration-${p.key}`}
              >
                <span className="text-sm text-slate-700 flex items-center gap-2">
                  {p.primary && (
                    <Icons.Star className="h-3.5 w-3.5 text-amber-400" />
                  )}
                  {p.name}
                </span>
                <span
                  className={`text-[11px] px-2 py-0.5 rounded-full capitalize ${
                    STATUS_TONE[p.status] || "bg-slate-100 text-slate-500"
                  }`}
                >
                  {p.status.replace("_", " ")}
                </span>
              </div>
            ))}
          </div>
        )}

        {/* Footer guidance */}
        <p className="text-xs text-slate-400">
          Zoom is the primary meeting platform. Enable scopes{" "}
          <code>meeting:write:admin</code>,{" "}
          <code>meeting:read:admin</code>,{" "}
          <code>user:read:admin</code> in your Zoom app, then set the host
          email above. Other providers are architected for future activation.
        </p>
      </div>
    </div>
  );
}
